import { Button, Input, message, Space } from 'antd';
import { useContext, useEffect, useState } from 'react'
import './UcsManage.less';
import Icon, { CaretRightOutlined } from '@ant-design/icons'
import { ReactComponent as WordSvg } from './icons/world.svg';
import { ReactComponent as AxisSvg } from './icons/axis.svg';
import classnames from 'classnames';
import { AxesPrimitive, PythaEventSetting, SETTING_KEY } from 'pytha2';
import { createAxes, createAxesMulti, deleteAxes, findAxesListInGraphic } from '@/declare/api/geometry/axes';
import { useParams } from 'react-router-dom';
import { CmdContext, EngineContext } from '@/App';

interface IProps {
    selectedUuid: string;
    setSelectedUuid: (uuid: string) => void;
    ucsList: AxesPrimitive[];
    setUcsList: (ucsList: AxesPrimitive[]) => void;
    onClose?: () => void;  //可从外部关闭的接口
}

/**
 * USE（用户坐标系）的管理界面(弹窗)
 * 实现显示、重命名、删除 UCS(目前使用假数据)
 * @returns 
 */
export default function UcsManage(props: IProps) {

    const { projectUuid } = useParams();
    const { engine } = useContext(EngineContext);
    const { selectedUuid, setSelectedUuid, ucsList, setUcsList } = props;
    const { updateCmdInputValue } = useContext(CmdContext);
    const [selectUuid, setSelectUuid] = useState<string>(null); // 当前选中的ucs的uuid，不是置为当前的ucs的uuid

    /**
     * 重命名 UCS 的 UUID
     */
    const [renamingUuid, setRenamingUuid] = useState<string | null>(null);

    useEffect(() => {
        setSelectUuid(selectedUuid);
    }, [])

    /**
     * 设置当前选中的 UCS 为活动 UCS
     */
    const setCurrentUcs = () => {
        if (!selectedUuid) return;
        const axes = ucsList.find(a => a.uuid === selectUuid);
        if (!axes || selectUuid === selectedUuid) return;
        setSelectedUuid(axes.uuid);
        console.log("切换 UCS:", axes.uuid);
        let pythaEvent = new PythaEventSetting();
        pythaEvent.data = { key: SETTING_KEY.AXES, value: axes }
        engine.eventMediator?.emitEvent(pythaEvent);
    };

    useEffect(() => {
        findAxesListInGraphic(projectUuid).then(res => {
            const jsonList: any[] = res.data || [];
            const axesList = [];
            for (const json of jsonList) {
                const axes = new AxesPrimitive();
                axes.fromJson(json.properties);
                axesList.push(axes);
            }
            const wcsIndex = axesList.findIndex(axes => axes.isWcs);
            // 从原数组删除，再加到头部
            if (wcsIndex > 0) {
                const wcs = axesList.splice(wcsIndex, 1)[0];
                axesList.unshift(wcs);
            }
            setUcsList(axesList);
        });
        if (!engine) return;
        setSelectedUuid(engine.storage.primitiveStore.axes.uuid)
    }, [])


    const addUcs = () => {
        if (engine) {
            engine.eventMediator.emitEvent({
                cmd: 'CMD',
                data: "UCS",
            });
            if(props.onClose){
                props.onClose();
            }
        }else{
            message.error("引擎未初始化");
        }
    }

    /**
     * 删除当前选中的 UCS
     */
    const onDelete = () => {
        if (!selectedUuid) {
            message.error("请选择一个 UCS 进行删除");
            return;
        }
        const ucs = ucsList.find((item) => item.uuid === selectUuid);
        if (ucs?.isWcs) {
            message.error("WCS 无法删除");
            return;
        }
        deleteAxes(selectUuid).then(() => {
            const newUcsList = ucsList.filter((item) => item.uuid !== selectUuid);
            setUcsList(newUcsList);
            const newSelectUuid = newUcsList[0].uuid;
            setSelectUuid(newSelectUuid);
            if (selectUuid === selectedUuid) {
                // 不考虑数组为空的情况，因为有wcs的存在
                let pythaEvent = new PythaEventSetting();
                pythaEvent.data = { key: SETTING_KEY.AXES, value: newUcsList[0] }
                engine.eventMediator?.emitEvent(pythaEvent);
                setSelectedUuid(newSelectUuid);
            }
            // message.success(`删除 UCS: ${ucs?.name}`);
        }).catch(err => {
            message.error('删除失败');
        })
    };
    /**
     * 激活当前选中的 UCS 重命名模式
     */
    const activeUcsRenaming = () => {
        const selectedUcs = ucsList.find((item) => item.uuid === selectUuid)
        if (!selectedUcs) return;
        if (selectedUcs.isWcs) {
            message.error("WCS 无法重命名");
            return;
        }
        updateCmdInputValue(true);
        setRenamingUuid(selectUuid);
    };
    /**
     * 修改 UCS 名称
     * @param ucsUuid 要修改的 UCS UUID
     * @param newName 新的名称
     */
    const changeUcsName = (ucsUuid: string, newName: string) => {
        if (!newName.trim()) {
            message.warning("名称不能为空");
            return;
        }
        const isDuplicate = ucsList.some(
            (item) => item.uuid !== ucsUuid && item.name === newName.trim()
        );
        if (isDuplicate) {
            message.warning(`名称 "${newName.trim()}" 已存在`);
            return;
        }
        const ucsIndex = ucsList.findIndex((item) => item.uuid === ucsUuid);
        if (ucsIndex === -1) return;
        const newUsc: AxesPrimitive = ucsList[ucsIndex];
        newUsc.name = newName;
        createAxes(projectUuid, newUsc.toJson()).then(res => {
            ucsList[ucsIndex] = newUsc;
            setUcsList(ucsList);
            // message.success(`重命名成功`);
        }).catch(err => {
            message.error(`重命名失败`);
        }).finally(() => {
            updateCmdInputValue(false);
            setRenamingUuid(null);
        })
    };

    return (
        <div className="ucs-manage-container">
            <div className="ucs-header">
                <span>当前UCS: </span>
                <span>{ucsList.find((ucs) => ucs.uuid === selectedUuid)?.name}</span>
            </div>
            <div className="ucs-body">
                <div className="axis-panel">
                    {ucsList.map((ucs) => (
                        <div key={ucs.uuid} className={classnames({
                            "ucs-item": true,
                            active: ucs.uuid === "1", // WCS 默认激活
                            selected: ucs.uuid === selectUuid,
                        })} onClick={() => {
                            setSelectUuid(ucs.uuid)
                        }}>
                            <div className="selected-arrow">
                                <CaretRightOutlined />
                            </div>
                            <div className="ucs-item-icon">
                                <Icon component={ucs.isWcs ? WordSvg : AxisSvg} />
                            </div>
                            <div className="ucs-item-name">
                                {renamingUuid === ucs.uuid && (
                                    <Input
                                        size="small"
                                        style={{ backgroundColor: '#fff' }}
                                        defaultValue={ucs.name}
                                        onBlur={(e) => changeUcsName(ucs.uuid, e.target.value)}
                                        //@ts-ignore
                                        onPressEnter={(e) => changeUcsName(ucs.uuid, e.target.value)}
                                    />
                                )}
                                {renamingUuid !== ucs.uuid && (ucs.name)}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="btn-panel">
                    <Space direction="vertical" size="small">
                        <Button size="small" style={{ width: '80px' }} onClick={addUcs}>新增</Button>
                        <Button size="small" style={{ width: '80px' }} onClick={setCurrentUcs}>置为当前</Button>
                        <Button size="small" style={{ width: '80px' }} onClick={activeUcsRenaming}>重命名</Button>
                        <Button size="small" danger style={{ width: '80px' }} onClick={onDelete}>删除</Button>
                    </Space>
                </div>
            </div>
        </div>
    )
}

